import React from "react";
import { Alert, Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { BeneficiaryInfo } from "../../../../interfaces/DTO/mislaka_xml_insurence_models";
import { RelationType } from "../../../../interfaces/DTO/mislaka_xml_pension_models";
import PercentageTextField from "../../../../shared/UIParts/InputFields/PercentageTextField";
import { formatRelationType } from "../../../../utils/pensionEnumFormatter";
import {
  BENEFICIARY_RELATION_OPTIONS,
  definitionProblem,
  newBeneficiary,
  totalPercentage,
} from "../../../../utils/beneficiariesUtil";

interface Props {
  value: BeneficiaryInfo[];
  onChange: (beneficiaries: BeneficiaryInfo[]) => void;
  /** the spouse and the children - offered as "הצע לפי המשפחה" */
  familySuggestion?: BeneficiaryInfo[];
  /** the client's last name - a beneficiary added by hand starts with it */
  defaultLastName?: string;
  disabled?: boolean;
}

/** הגדרת מוטבים למקרה מוות: זיקה, שם, ת"ז ואחוז – עם סיכום האחוזים והצעה לפי בני המשפחה */
const BeneficiariesDefinitionEditor: React.FC<Props> = ({ value, onChange, familySuggestion = [], defaultLastName = "", disabled }) => {
  const update = (index: number, fields: Partial<BeneficiaryInfo>) =>
    onChange(value.map((b, i) => (i === index ? { ...b, ...fields } : b)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  const problem = value.length > 0 ? definitionProblem(value) : null;
  const total = totalPercentage(value);

  // a beneficiary added by hand - the client's last name; the first one gets all 100%
  const addBeneficiary = () =>
    onChange([
      ...value,
      { ...newBeneficiary(), last_name: defaultLastName, beneficiary_percentage: value.length === 0 ? 100 : 0 },
    ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {value.map((b, index) => (
        // one line per beneficiary - the fields shrink to fit, the delete stays on the line
        <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <FormControl size="small" sx={{ width: 90, flexShrink: 0 }}>
            <InputLabel>זיקה</InputLabel>
            <Select
              label="זיקה"
              value={b.relation_type ?? RelationType.OTHER}
              onChange={(e) => update(index, { relation_type: Number(e.target.value) })}
              disabled={disabled}
            >
              {BENEFICIARY_RELATION_OPTIONS.map((r) => (
                <MenuItem key={r} value={r}>
                  {formatRelationType(r)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="שם פרטי"
            value={b.first_name ?? ""}
            onChange={(e) => update(index, { first_name: e.target.value })}
            disabled={disabled}
            sx={{ flex: 1, minWidth: 70 }}
          />
          <TextField
            size="small"
            label="שם משפחה"
            value={b.last_name ?? ""}
            onChange={(e) => update(index, { last_name: e.target.value })}
            disabled={disabled}
            sx={{ flex: 1, minWidth: 70 }}
          />
          <TextField
            size="small"
            label="ת.ז"
            value={b.id_number ?? ""}
            onChange={(e) => update(index, { id_number: e.target.value.replace(/\D/g, "") })}
            disabled={disabled}
            inputProps={{ inputMode: "numeric", maxLength: 9 }}
            sx={{ flex: 1.2, minWidth: 90 }}
          />
          <Box sx={{ width: 80, flexShrink: 0 }}>
            <PercentageTextField
              size="small"
              label="אחוז"
              value={b.beneficiary_percentage ?? 0}
              onChange={(pct) => update(index, { beneficiary_percentage: pct })}
              decimalPlaces={0}
              disabled={disabled}
            />
          </Box>
          <IconButton size="small" onClick={() => remove(index)} disabled={disabled} aria-label="הסר מוטב" sx={{ flexShrink: 0 }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      {/* the total - right under the rows, at the right */}
      {value.length > 0 && (
        <Typography variant="body2" fontWeight={700} color={Math.abs(total - 100) > 0.01 ? "error.main" : "success.main"}>
          סה"כ {total}%
        </Typography>
      )}

      {value.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          לא הוגדרו מוטבים.
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <Button size="small" color="secondary" startIcon={<AddIcon sx={{ ml: 1 }} />} onClick={addBeneficiary} disabled={disabled}>
          הוסף מוטב
        </Button>
        {familySuggestion.length > 0 && (
          <Button size="small" color="secondary" startIcon={<AutoFixHighIcon sx={{ ml: 1 }} />} onClick={() => onChange(familySuggestion)} disabled={disabled}>
            הצע לפי המשפחה (בן/בת זוג וילדים)
          </Button>
        )}
      </Box>

      {problem && <Alert severity="warning">{problem}</Alert>}
    </Box>
  );
};

export default BeneficiariesDefinitionEditor;
